import { buttonStyle } from 'components/buttons/buttonStyle';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput.tsx';
import DynamicMultiSelectInput from 'components/forms/inputs/rhf-inputs/DynamicMultiSelectInput';
import { DataTable } from 'components/tables/data-table';
import { Button } from 'components/ui/button';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees.ts';
import { useUpdateDefaultHandledByEmployeesMutation } from 'store/api/tenants.ts';
import { NamedEntityMinDto } from 'types/other.ts';
import { DefaultHandledByEmployeesUpdateDto, TenantConfigurationDto } from 'types/tenants.ts';
import { t } from 'utils/getTranslations.ts';
import { useHasPermissions } from 'utils/permissionUtil';

interface Props {
    tenantConfiguration: TenantConfigurationDto;
}

interface DefaultHandledByForm {
    defaultHandledByEmployees: NamedEntityMinDto[];
    isDefaultHandledByOnlyWhenConfidential: boolean;
}

const DefaultHandledByEmployeesControl = ({ tenantConfiguration }: Props): JSX.Element => {
    const [updateDefaultHandledByEmployees] = useUpdateDefaultHandledByEmployeesMutation();
    const hasUpdatePermission = useHasPermissions(['UPDATE.BACK_OFFICE_SETTINGS']);

    const stored: DefaultHandledByForm = {
        defaultHandledByEmployees: tenantConfiguration.defaultHandledByEmployees,
        isDefaultHandledByOnlyWhenConfidential: tenantConfiguration.isDefaultHandledByOnlyWhenConfidential,
    };

    // RHF `values` keeps the form synced with the configuration the mutation responds with,
    // replacing a useEffect(setValue) bridge.
    const { control, setValue, handleSubmit } = useForm<DefaultHandledByForm>({
        defaultValues: stored,
        values: stored,
    });

    const columns = useMemo<DataTableColumnDef<TenantConfigurationDto>[]>(
        () => [
            {
                header: t('configuration.default-handled-by'),
                id: 'default-handled-by',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    // Room for two chips in the picker beside the one-line box label and Save.
                    minPx: 680,
                },
                cell: () => (
                    <div className="flex flex-row gap-4">
                        <DynamicMultiSelectInput
                            control={control}
                            name="defaultHandledByEmployees"
                            placeholder="matters.select-employee"
                            paginated={{
                                fetch: useLazyGetEmployeesListWithFiltersQuery,
                                searchField: 'fullName',
                            }}
                            getOptionLabel="name"
                            getOptionIdentifier="id"
                            setValue={setValue}
                            disabled={!hasUpdatePermission}
                        />
                        <CheckboxInput
                            control={control}
                            name="isDefaultHandledByOnlyWhenConfidential"
                            label="configuration.default-handled-by-when-confidential"
                            wrapperClassName="min-h-9 whitespace-nowrap"
                            disabled={!hasUpdatePermission}
                        />
                        <Button
                            {...buttonStyle({ variant: 'contained', color: 'secondary' }, 'h-9')}
                            onClick={() => {
                                void handleSubmit((data) => {
                                    const updateDto: DefaultHandledByEmployeesUpdateDto = {
                                        defaultHandledByEmployeeIds: data.defaultHandledByEmployees.map(
                                            (employee) => employee.id
                                        ),
                                        isDefaultHandledByOnlyWhenConfidential:
                                            data.isDefaultHandledByOnlyWhenConfidential,
                                    };
                                    updateDefaultHandledByEmployees(updateDto);
                                })();
                            }}
                            disabled={!hasUpdatePermission}
                        >
                            <FormattedMessage id="save" />
                        </Button>
                    </div>
                ),
            },
            {
                header: '',
                id: 'spacer',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                },
                cell: () => null,
            },
        ],
        [control, setValue, handleSubmit, hasUpdatePermission, updateDefaultHandledByEmployees]
    );

    const rows = useMemo(() => [tenantConfiguration], [tenantConfiguration]);

    return (
        <>
            <div className="flex flex-row justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.default-handled-by" />
                </h3>
            </div>
            <DataTable<TenantConfigurationDto>
                columns={columns}
                data={rows}
                columnConfigId="configuration-default-handled-by"
            />
        </>
    );
};

export default DefaultHandledByEmployeesControl;
